import fp from 'lodash/fp';
import React, { useCallback, useMemo, ComponentType, ComponentProps } from 'react';
import ReactSelect, { components as SelectComponents, Props as ReactSelectProps, OptionProps } from 'react-select';
import ReactSelectAsync from 'react-select/async';

import { DropdownArrow } from 'Modules/ui/Icons';

import { Text } from '../Typography';
import { ClassName, cn, setHtml } from '../../utils';
import styles from './index.module.scss';


type Option<TValue> = {
    label: string;
    value: TValue;
    disabled?: boolean;
};

type Variant = 'field' | 'standalone' | 'auxiliary';
type Color = 'standard' | 'black';

export type Select2Props<TValue = any> = ClassName &
Omit<ReactSelectProps,
'className' |
'classNamePrefix' |
'value' |
'onChange' |
'options' |
'placeholder' |
'isDisabled' |
'isOptionDisabled' |
'isClearable' |
'isSearchable' |
'noOptionsMessage' |
'menuPortalTarget' |
'menuPosition' |
'menuPlacement'
> & {
    variant?: Variant;
    color?: Color;
    value: TValue;
    options?: Option<TValue>[];
    onChange: (value: TValue) => void;
    disabled?: boolean;
    clearable?: boolean;
    searchable?: boolean;
    placeholder?: string;
    ellipsis?: boolean;
    forceDirection?: 'bottom' | 'top';
    noOptionsMessage?: string;
};

type AsyncProps = {
    loadOptions: (value: string) => void;
    onInputChange?: (value: string) => void;
    cacheOptions?: boolean;
}

type AsyncSelectProps<TValue> = Select2Props<TValue> & AsyncProps;

const CustomDropdown = ({ variant, isDisabled }:
ComponentProps<typeof SelectComponents.DropdownIndicator>) => (
    <div className={cn(styles.dropdown, styles[variant], isDisabled && styles.disabled)}>
        <DropdownArrow />
    </div>
);

export const CustomMenu = ({ placement, variant, className, color, ...props }:
ComponentProps<typeof SelectComponents.Menu>) => (
    <SelectComponents.Menu
        className={cn(
            styles.menu,
            variant !== 'auxiliary' && styles[color],
            styles[variant],
            styles[placement],
            className,
        )}
        placement={placement}
        {...props}
    />
);

const CustomNoOptionsMessage = props => (
    <Text component={SelectComponents.NoOptionsMessage} {...props} />
);

type OptionBase = OptionProps<any, false>;

export const CustomOption = React.forwardRef<
HTMLDivElement,
Pick<OptionBase, 'isDisabled' | 'isSelected' | 'isFocused' | 'children'> & OptionBase['innerProps']
>(({
    children,
    isDisabled,
    isSelected,
    isFocused,
    ...props
}, ref) => (
    <div
        className={cn(
            styles.item,
            isSelected && styles.selected,
            isFocused && styles.focused,
            isDisabled && styles.disabled,
        )}
        ref={ref}
        {...props}
    >
        {children}
    </div>
));

const DefaultOption: ComponentType<OptionProps<any, false>> = React.memo(({
    data: { label },
    innerProps,
    innerRef,
    isDisabled,
    isSelected,
    isFocused,
}) => (
    <CustomOption
        isDisabled={isDisabled}
        isSelected={isFocused}
        isFocused={isSelected}
        ref={innerRef}
        {...innerProps}
    >
        <Text
            component='div'
            ellipsis
            className={styles.name}
            title={label}
            {...setHtml(label)}
        />
    </CustomOption>
));

const CustomSingleValue = ({ children, ...props }) => {
    const { getValue } = props;
    const [{ label }] = getValue();

    return (
        <SelectComponents.SingleValue {...props}>
            <Text
                component='div'
                ellipsis
                className={styles.name}
                title={label}
                {...setHtml(label)}
            />
        </SelectComponents.SingleValue>
    );
};

const withSelectProps = <TValue extends any, TProps extends Select2Props<TValue>>(
    SelectComponent: ComponentType<ReactSelectProps<Option<TValue>>>,
) => React.forwardRef<any, TProps>(({
    className,
    variant,
    value,
    options,
    onChange,
    error,
    disabled,
    clearable,
    searchable,
    placeholder,
    components,
    noOptionsMessage,
    color = 'standard',
    ...props
}, ref) => {
    const noOptionsMessageCallback = useCallback(
        () => noOptionsMessage,
        [noOptionsMessage],
    );

    const Menu = useCallback(props =>
        <CustomMenu {...props} variant={variant} color={color} />, [color, variant]);
    const DropdownIndicator = useCallback(() =>
        <CustomDropdown variant={variant} isDisabled={disabled} />, [disabled, variant]);

    return (
        <SelectComponent
            ref={ref}
            className={cn(
                styles.select,
                variant !== 'auxiliary' && styles[color],
                styles[variant],
                className,
                error && styles.error,
            )}
            classNamePrefix='react-select'
            value={value}
            onChange={onChange}
            options={options}
            placeholder={placeholder}
            isDisabled={disabled}
            isOptionDisabled={fp.get('disabled')}
            isClearable={clearable}
            isSearchable={searchable}
            noOptionsMessage={noOptionsMessageCallback}
            components={{
                Option: DefaultOption,
                IndicatorSeparator: clearable && (value && !options)
                    ? SelectComponents.IndicatorSeparator
                    : null,
                Menu,
                DropdownIndicator,
                SingleValue: CustomSingleValue,
                NoOptionsMessage: CustomNoOptionsMessage,
                ...components,
            }}
            menuPosition='absolute'
            menuPortalTarget={document.getElementById('ui-portal')}
            closeMenuOnScroll
            menuPlacement='auto'
            {...props}
        />
    );
});

export const Select2 = withSelectProps<any, Select2Props<any>>(
    React.forwardRef<any, any>(({
        value,
        onChange,
        options,
        ...props
    }, ref) => {
        const selectedValue = useMemo(
            () => (fp.isArray(value)
                ? fp.map(key => fp.find(({ value: v }) => v === key, options), value)
                : fp.find(({ value: v }) => v === value, options)) || null,
            [value, options],
        );

        const onChangeCallback = useCallback(
            option => onChange(option
                ? fp.isArray(option) ? fp.map(fp.get('value'), option) : option.value
                : null),
            [onChange],
        );

        return (
            <ReactSelect
                ref={ref}
                value={selectedValue}
                onChange={onChangeCallback}
                options={options}
                {...props}
            />
        );
    }),
);

export const AsyncSelect = withSelectProps<any, AsyncSelectProps<any>>(
    React.forwardRef<any, any>(({
        loadOptions,
        onInputChange,
        cacheOptions = true,
        ...props
    }, ref) => (
        <ReactSelectAsync
            ref={ref}
            defaultOptions
            loadOptions={loadOptions}
            onInputChange={onInputChange}
            cacheOptions={cacheOptions}
            {...props}
        />
    )),
);
